// components/EventRegistrationForm/ReviewSection.js
import React, { useState } from 'react';
import {
    CheckCircle,
    CreditCard,
    Edit2,
    FileText,
    Building,
    MapPin,
    Percent,
    IndianRupee,
    Info,
    Check,
    X,
    Save,
    AlertCircle
} from 'lucide-react';
import Swal from 'sweetalert2';
import { postData } from '../../../../services/apiService';

const ReviewSection = ({
    event,
    isGroupEvent,
    groupMembers,
    formData,
    totalAmount,
    registrationId,
    editForm,
    setShowTermsModal
}) => {
    const [showTDSModal, setShowTDSModal] = useState(false);
    const [showGSTModal, setShowGSTModal] = useState(false);
    const [hasTDS, setHasTDS] = useState(false);
    const [hasGST, setHasGST] = useState(false);
    const [tdsPercentage, setTdsPercentage] = useState('');
    const [gstNumber, setGstNumber] = useState('');
    const [gstAddress, setGstAddress] = useState('');
    const [isSubmittingTDS, setIsSubmittingTDS] = useState(false);
    const [isSubmittingGST, setIsSubmittingGST] = useState(false);

    const openPrivacyPolicy = () => {
        window.open("/EMS/privacy-policy", "_blank", "noopener,noreferrer");
    };

    // Handle TDS checkbox change
    const handleTDSChange = (e) => {
        const isChecked = e.target.checked;

        if (isChecked) {
            setShowTDSModal(true);
        } else {
            // Clear TDS data when unchecked with confirmation
            Swal.fire({
                title: 'Remove TDS?',
                text: 'Are you sure you want to remove TDS deduction?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, remove',
                cancelButtonText: 'Cancel',
                background: '#f8fafc',
                color: '#1f2937'
            }).then((result) => {
                if (result.isConfirmed) {
                    setHasTDS(false);
                    setTdsPercentage('');
                    Swal.fire({
                        title: 'TDS Removed',
                        text: 'TDS deduction has been removed',
                        icon: 'success',
                        timer: 1500,
                        showConfirmButton: false
                    });
                } else {
                    // If user cancels, re-check the checkbox
                    e.target.checked = true;
                }
            });
        }
    };

    // Handle GST checkbox change
    const handleGSTChange = (e) => {
        const isChecked = e.target.checked;

        if (isChecked) {
            setShowGSTModal(true);
        } else {
            // Clear GST data when unchecked with confirmation
            Swal.fire({
                title: 'Remove GST Bill?',
                text: 'Are you sure you want to remove GST bill details?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, remove',
                cancelButtonText: 'Cancel',
                background: '#f8fafc',
                color: '#1f2937'
            }).then((result) => {
                if (result.isConfirmed) {
                    setHasGST(false);
                    setGstNumber('');
                    setGstAddress('');
                    Swal.fire({
                        title: 'GST Details Removed',
                        text: 'GST bill details have been removed',
                        icon: 'success',
                        timer: 1500,
                        showConfirmButton: false
                    });
                } else {
                    // If user cancels, re-check the checkbox
                    e.target.checked = true;
                }
            });
        }
    };

    // Save TDS percentage
    const saveTDS = async () => {
        if (!tdsPercentage || tdsPercentage <= 0 || tdsPercentage > 100) {
            Swal.fire({
                title: 'Invalid Percentage',
                text: 'Please enter a valid TDS percentage between 1 and 100',
                icon: 'warning',
                confirmButtonText: 'OK',
                background: '#f8fafc',
                color: '#1f2937'
            });
            return;
        }

        setIsSubmittingTDS(true);

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));

        setIsSubmittingTDS(false);
        setShowTDSModal(false);
        setHasTDS(true); // Ensure TDS is set to true here

        Swal.fire({
            title: 'TDS Added Successfully!',
            html: `
                <div class="text-left">
                    <p class="mb-2"><strong>TDS Percentage:</strong> ${tdsPercentage}%</p>
                    <p><strong>Deduction Amount:</strong> ₹${((totalAmount * parseFloat(tdsPercentage)) / 100).toFixed(2)}</p>
                </div>
            `,
            icon: 'success',
            timer: 2500,
            showConfirmButton: false,
            background: '#f8fafc',
            color: '#1f2937'
        });
    };

    // Cancel TDS modal
    const cancelTDS = () => {
        setShowTDSModal(false);
        // If no TDS percentage was entered, ensure TDS is false
        if (!tdsPercentage) {
            setHasTDS(false);
        } else {
            // If there was a percentage entered but user cancels, reset checkbox state
            setHasTDS(false);
            setTdsPercentage('');
        }
    };

    // Cancel GST modal
    const cancelGST = () => {
        setShowGSTModal(false);
        if (!gstNumber && !gstAddress) {
            setHasGST(false);
        } else {
            setHasGST(false);
            setGstNumber('');
            setGstAddress('');
        }
    };

    // Save GST details
    const saveGST = async () => {
        if (!gstNumber || gstNumber.trim() === '') {
            Swal.fire({
                title: 'GST Number Required',
                text: 'Please enter a valid GST number',
                icon: 'warning',
                confirmButtonText: 'OK',
                background: '#f8fafc',
                color: '#1f2937'
            });
            return;
        }

        // Basic GST number validation (15 characters: 2 digits, 10 PAN, 1 digit, 1 checksum)
        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        if (!gstRegex.test(gstNumber.toUpperCase())) {
            Swal.fire({
                title: 'Invalid GST Number',
                text: 'Please enter a valid GST number format (e.g., 22AAAAA0000A1Z5)',
                icon: 'warning',
                confirmButtonText: 'OK',
                background: '#f8fafc',
                color: '#1f2937'
            });
            return;
        }

        if (!gstAddress || gstAddress.trim() === '') {
            Swal.fire({
                title: 'GST Address Required',
                text: 'Please enter the GST billing address',
                icon: 'warning',
                confirmButtonText: 'OK',
                background: '#f8fafc',
                color: '#1f2937'
            });
            return;
        }

        setIsSubmittingGST(true);

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));

        setIsSubmittingGST(false);
        setShowGSTModal(false);
        setHasGST(true); // Ensure GST is set to true here

        Swal.fire({
            title: 'GST Details Saved!',
            html: `
                <div class="text-left">
                    <p class="mb-2"><strong>GST Number:</strong> ${gstNumber.toUpperCase()}</p>
                    <p><strong>Billing Address:</strong> ${gstAddress}</p>
                </div>
            `,
            icon: 'success',
            timer: 2500,
            showConfirmButton: false,
            background: '#f8fafc',
            color: '#1f2937'
        });
    };

    // Calculate final amount after TDS
    const getFinalAmount = () => {
        if (hasTDS && tdsPercentage) {
            const tdsAmount = (totalAmount * parseFloat(tdsPercentage)) / 100;
            return totalAmount - tdsAmount;
        }
        return totalAmount;
    };

    // Calculate TDS amount
    const getTDSAmount = () => {
        if (hasTDS && tdsPercentage) {
            return (totalAmount * parseFloat(tdsPercentage)) / 100;
        }
        return 0;
    };

    return (
        <>
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-200 animate-fadeIn">
                {/* Header with Animation */}
                <div className="text-center mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <CheckCircle className="w-10 h-10 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-2">
                        Review Your Registration
                    </h2>
                    <p className="text-gray-600 text-base">
                        Please review your details before {event.IsPaidEvent ? 'proceeding to payment' : 'completing registration'}
                    </p>
                </div>

                {/* Main Info Cards */}
                <div className="grid lg:grid-cols-2 gap-6 mb-6">
                    {/* Registration Details Card */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200 shadow-sm hover:shadow-md transition-shadow">
                        <h4 className="font-bold text-gray-800 mb-4 text-lg flex items-center border-b border-blue-200 pb-2">
                            <IndianRupee className="w-5 h-5 mr-2 text-blue-600" />
                            Registration Details
                        </h4>
                        <div className="space-y-3 text-sm">
                            {isGroupEvent ? (
                                <>
                                    <div className="flex justify-between items-center py-1">
                                        <span className="text-gray-600 font-medium">Event Name:</span>
                                        <span className="font-semibold text-gray-800">{event.Event_Name}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-1">
                                        <span className="text-gray-600 font-medium">Total Members:</span>
                                        <span className="font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded-full text-xs">
                                            {groupMembers.length} Members
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-1">
                                        <span className="text-gray-600 font-medium">Primary Member:</span>
                                        <span className="font-medium text-gray-800">{groupMembers[0]?.fullName || ''}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-1">
                                        <span className="text-gray-600 font-medium">Contact Email:</span>
                                        <span className="font-medium text-gray-800 break-all text-right">{groupMembers[0]?.email || ''}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-1">
                                        <span className="text-gray-600 font-medium">Contact Phone:</span>
                                        <span className="font-medium text-gray-800">{groupMembers[0]?.phone || ''}</span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="flex justify-between items-center py-1">
                                        <span className="text-gray-600 font-medium">Full Name:</span>
                                        <span className="font-semibold text-gray-800">{formData.fullName}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-1">
                                        <span className="text-gray-600 font-medium">Email:</span>
                                        <span className="font-medium text-gray-800 break-all text-right">{formData.email}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-1">
                                        <span className="text-gray-600 font-medium">Phone:</span>
                                        <span className="font-medium text-gray-800">{formData.phone}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-1">
                                        <span className="text-gray-600 font-medium">Participant Type:</span>
                                        <span className="font-medium text-gray-800">{formData.role}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-1">
                                        <span className="text-gray-600 font-medium">Event:</span>
                                        <span className="font-medium text-gray-800">{event.Event_Name}</span>
                                    </div>
                                </>
                            )}
                            {registrationId && (
                                <div className="flex justify-between items-center py-1 mt-2 pt-2 border-t border-blue-200">
                                    <span className="text-gray-600 font-medium">Registration ID:</span>
                                    <span className="font-mono text-sm font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded">{registrationId}</span>
                                </div>
                            )}
                            <div className="flex justify-between items-center py-1">
                                <span className="text-gray-600 font-medium">Verification Status:</span>
                                <span className="flex items-center gap-1">
                                    <Check className="w-4 h-4 text-green-500" />
                                    <span className="text-green-600 font-medium">Phone & Email Verified</span>
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Payment Summary Card */}
                    <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-5 border border-yellow-200 shadow-sm hover:shadow-md transition-shadow">
                        <h4 className="font-bold text-gray-800 mb-4 text-lg flex items-center border-b border-yellow-200 pb-2">
                            <CreditCard className="w-5 h-5 mr-2 text-yellow-600" />
                            Payment Summary
                        </h4>
                        <div className="space-y-3">
                            {isGroupEvent && groupMembers.length > 0 && (
                                <div className="mb-3">
                                    <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Fee Breakdown</p>
                                    <div className="bg-white rounded-lg p-3 max-h-32 overflow-y-auto space-y-1">
                                        {groupMembers.map((member, index) => (
                                            member.role && (
                                                <div key={index} className="flex justify-between items-center text-sm">
                                                    <span className="text-gray-600">Member {index + 1} ({member.role}):</span>
                                                    <span className="font-semibold text-gray-800">₹{member.amount || 0}</span>
                                                </div>
                                            )
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between items-center py-2">
                                <span className="text-gray-600">Registration Fee:</span>
                                <span className="font-bold text-gray-800">₹{totalAmount}</span>
                            </div>

                            {/* TDS Display with Animation */}
                            {hasTDS && tdsPercentage && (
                                <div className="bg-red-50 rounded-lg p-3 border border-red-200 animate-slideDown">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 flex items-center gap-2">
                                            <Percent className="w-4 h-4 text-red-500" />
                                            <span>TDS Deduction ({tdsPercentage}%)</span>
                                        </span>
                                        <span className="font-semibold text-red-600">- ₹{getTDSAmount().toFixed(2)}</span>
                                    </div>
                                </div>
                            )}

                            {/* Final Amount after TDS */}
                            {hasTDS && tdsPercentage && (
                                <div className="bg-green-50 rounded-lg p-3 border border-green-200 animate-slideDown">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-semibold">Amount after TDS:</span>
                                        <span className="font-bold text-green-600 text-lg">₹{getFinalAmount().toFixed(2)}</span>
                                    </div>
                                </div>
                            )}

                            {/* <div className="flex justify-between items-center py-2">
                                <span className="text-gray-600">Payment Status:</span>
                                <span className={`font-semibold px-3 py-1 rounded-full text-sm ${event.IsPaidEvent ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                                    {event.IsPaidEvent ? 'Pending Payment' : 'Free Registration'}
                                </span>
                            </div> */}

                            <div className="border-t-2 border-yellow-200 pt-3 mt-2">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-800 font-bold text-lg">Total Amount:</span>
                                    <span className={`font-bold text-2xl ${event.IsPaidEvent ? 'text-yellow-600' : 'text-green-600'}`}>
                                        ₹{hasTDS && tdsPercentage ? getFinalAmount().toFixed(2) : totalAmount}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Additional Options Section - Enhanced UI Hidden by SuryaPrakash/Nitheesh TDS Column*/}
                {/* <div className="mb-6">
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200">
                        <h4 className="font-bold text-gray-800 mb-4 text-lg flex items-center">
                            <FileText className="w-5 h-5 mr-2 text-blue-600" />
                            Additional Options
                        </h4>


                            <div className={`relative bg-white group ${hasTDS ? 'ring-2 ring-green-400 ring-opacity-50' : ''}`}>
                                <div className={`bg-white rounded-xl p-4  transition-all duration-300 cursor-pointer border-none'}`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start space-x-3 flex-1">
                                            <input
                                                type="checkbox"
                                                checked={hasTDS}
                                                onChange={handleTDSChange}
                                                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 mt-0.5 cursor-pointer"
                                            />
                                            <div className="flex-1">
                                                <label className="text-sm font-semibold text-gray-800 cursor-pointer flex items-center gap-2">
                                                    <Percent className="w-4 h-4 text-green-600" />
                                                    Do You Have TDS?
                                                </label>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Apply TDS deduction on registration amount
                                                </p>
                                                {hasTDS && tdsPercentage && (
                                                    <div className="mt-2 flex items-center gap-2">
                                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                                                            <Check className="w-3 h-3" />
                                                            {tdsPercentage}% TDS Applied
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {!hasTDS && (
                                            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Info className="w-4 h-4 text-blue-400" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                           
                            <div className={`relative group ${hasGST ? 'ring-2 ring-blue-400 ring-opacity-50' : ''}`}>
                                <div className={`bg-white rounded-xl p-4 border-2 transition-all duration-300 hover:shadow-lg cursor-pointer`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start space-x-3 flex-1">
                                            <input
                                                type="checkbox"
                                                checked={hasGST}
                                                onChange={handleGSTChange}
                                                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5 cursor-pointer"
                                            />
                                            <div className="flex-1">
                                                <label className="text-sm font-semibold text-gray-800 cursor-pointer flex items-center gap-2">
                                                    <IndianRupee className="w-4 h-4 text-blue-600" />
                                                    Do You Want GST Bill?
                                                </label>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Generate GST invoice for your registration
                                                </p>
                                                {hasGST && gstNumber && (
                                                    <div className="mt-2 space-y-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full flex items-center gap-1">
                                                                <Building className="w-3 h-3" />
                                                                GST: {gstNumber}
                                                            </span>
                                                        </div>
                                                        {gstAddress && (
                                                            <p className="text-xs text-gray-600 mt-1 flex items-start gap-1">
                                                                <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                                                <span className="break-all">{gstAddress}</span>
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {!hasGST && (
                                            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Info className="w-4 h-4 text-blue-400" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        
                        </div>


                        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-start gap-2">
                                <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-blue-700">
                                    {isGroupEvent && event.IsPaidEvent
                                        ? "TDS deduction will be applied to the total amount. GST bill will be generated with your provided details."
                                        : "GST bill will be generated with your provided details for billing purposes."}
                                </p>
                            </div>
                        </div>
                    </div>
                </div> */}

                {/* Privacy Policy Link */}
                <div className="mb-6 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-full">
                        <p className="text-xs text-gray-600">
                            By proceeding, you agree to our{' '}
                            <button
                                onClick={openPrivacyPolicy}
                                className="text-green-600 hover:text-green-800 font-medium underline transition-colors"
                            >
                                Privacy Policy
                            </button>
                        </p>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="text-center">
                    <div className="flex flex-wrap justify-center gap-4">
                        <button
                            onClick={editForm}
                            className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-200 flex items-center gap-2 hover:border-gray-400"
                        >
                            <Edit2 className="w-5 h-5" />
                            Edit Details
                        </button>

                        <button
                            onClick={() => setShowTermsModal(true)}
                            className="px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center gap-2"
                        >
                            <CreditCard className="w-5 h-5" />
                            Proceed to Payment
                        </button>
                    </div>
                </div>
            </div>

            {/* Enhanced TDS Modal */}
            {showTDSModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all animate-scaleIn">
                        <div className="bg-gradient-to-r from-blue-500 to-indigo-800 rounded-t-2xl p-4">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                <Percent className="w-5 h-5" />
                                Add TDS Details
                            </h3>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-gray-600 mb-4">
                                Enter the TDS percentage to be deducted from the total amount.
                            </p>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    TDS Percentage (%) <span className="text-blue-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={tdsPercentage}
                                    onChange={(e) => setTdsPercentage(e.target.value)}
                                    onKeyDown={(e) => {
                                        // Allow only numbers, decimal point, and control keys
                                        if (!/^[0-9.]$/.test(e.key) &&
                                            !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                        // Prevent multiple dots
                                        if (e.key === '.' && e.target.value.includes('.')) {
                                            e.preventDefault();
                                        }
                                    }}
                                    placeholder="Enter TDS percentage (e.g., 10)"
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-ingigo-500 outline-none transition-all"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    autoFocus
                                />
                                <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Total Amount:</span>
                                        <span className="font-semibold">₹{totalAmount}</span>
                                    </div>
                                    <div className="flex justify-between text-sm mt-1">
                                        <span className="text-gray-600">TDS Amount:</span>
                                        <span className="font-semibold text-red-600">
                                            ₹{tdsPercentage ? ((totalAmount * parseFloat(tdsPercentage)) / 100).toFixed(2) : '0.00'}
                                        </span>
                                    </div>
                                    <div className="border-t mt-2 pt-2">
                                        <div className="flex justify-between font-bold">
                                            <span>Amount Payable:</span>
                                            <span className="text-green-600">
                                                ₹{tdsPercentage ? (totalAmount - (totalAmount * parseFloat(tdsPercentage)) / 100).toFixed(2) : totalAmount}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={cancelTDS}
                                    className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors flex items-center gap-2"
                                >
                                    <X className="w-4 h-4" />
                                    Cancel
                                </button>
                                <button
                                    onClick={saveTDS}
                                    disabled={isSubmittingTDS}
                                    className="px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-800 text-white rounded-xl hover:from-indigo-800 hover:to-blue-500 transition-all flex items-center gap-2 disabled:opacity-50"
                                >
                                    {isSubmittingTDS ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            Save TDS
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Enhanced GST Modal */}
            {showGSTModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all animate-scaleIn">
                        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-t-2xl p-4">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                <IndianRupee className="w-5 h-5" />
                                GST Bill Details
                            </h3>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-gray-600 mb-4">
                                Enter your GST details for billing purposes.
                            </p>

                            <div className="space-y-4 mb-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        GST Number <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={gstNumber}
                                        onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
                                        placeholder="e.g., 22AAAAA0000A1Z5"
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all uppercase"
                                        autoFocus
                                    />
                                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        Format: 15 characters (2 digits, 10 PAN, 1 digit, 1 checksum)
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        GST Billing Address <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        value={gstAddress}
                                        onChange={(e) => setGstAddress(e.target.value)}
                                        placeholder="Enter complete GST billing address"
                                        rows="3"
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={cancelGST}
                                    className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors flex items-center gap-2"
                                >
                                    <X className="w-4 h-4" />
                                    Cancel
                                </button>
                                <button
                                    onClick={saveGST}
                                    disabled={isSubmittingGST}
                                    className="px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all flex items-center gap-2 disabled:opacity-50"
                                >
                                    {isSubmittingGST ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            Save GST Details
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ReviewSection;